import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import dataService from "../../../helpers/dataService";
import tools from "../../../helpers/tools";
import { estAnonymise } from "../../../helpers/allocataire";
import { FactList, FicheHeader, FicheSection } from "../../common/Fiche";
import { Pill } from "../../common/DataList";
import { MOTIFS_REFUS, STATUT_TONES, demandeur, nomComplet } from "./suspensionRemobilisationLabels";
import "./suspension-remobilisation.scss";

// Détail d'une Suspension / Remobilisation dans le panneau latéral : ce que
// le Directeur doit savoir pour trancher, et les deux boutons de décision.
// Kit Fiche (en-tête, faits, sections) au rythme d'une fiche en panneau.
const DetailDemandeSuspensionRemobilisation = ({ demande: d, onDone = () => {} }) => {
  const [motifRefus, setMotifRefus] = useState("");
  const [refusOuvert, setRefusOuvert] = useState(false);
  const [confirmationOuverte, setConfirmationOuverte] = useState(false);
  const [enCours, setEnCours] = useState(null);

  // Pas de commentaire libre (RGPD) : la validation part sans corps, le refus
  // porte un motif de la liste fermée.
  const decider = (action) => {
    setEnCours(action);
    dataService.post(
      `sanctions/suspension-remobilisation/${d.id}/${action}`,
      action === "refus" ? { reason: motifRefus } : {},
      (reponse) => {
        const retrait = reponse?.epRegistrationWithdrawal;
        if (action === "refus") toast.success("Demande refusée");
        else if (!retrait) toast.success("Demande validée");
        else if (retrait.done) toast.success(`Demande validée : inscription à l'EP du ${tools.formatDate(retrait.epDate)} retirée`);
        else toast.warning(`Demande validée, mais l'inscription n'a pas été retirée : ${retrait.reasonNotDone}`);
        onDone();
      },
      (err) => toast.error(err?.message || "La décision n'a pas pu être enregistrée"),
      () => setEnCours(null)
    );
  };

  const s = d.targetedSanction;
  const i = d.targetedEpRegistration;
  const r = d.epRegistrationWithdrawal;
  const enAttente = d.status === "EN_ATTENTE_VALIDATION";
  // Un dossier anonymisé n'accepte plus aucune action : la demande reste
  // consultable, mais la section de décision n'est pas proposée.
  const decidable = enAttente && !estAnonymise(d.recipient);
  const saisine = d.outcome === "LEVEE_DE_SAISINE";

  return (
    <div className="detail-demande-suspension-remobilisation fiche-page fiche-page--modal">
      <FicheHeader
        headingLevel="h2"
        eyebrow={d.outcomeLabel}
        title={
          d.recipient ? (
            <Link to={tools.getRecipientLinkFromParams(d.recipient.type, d.recipient.id)} target="_blank" rel="noopener noreferrer">
              {nomComplet(d.recipient)}
            </Link>
          ) : (
            "Allocataire non rattaché"
          )
        }
        pills={<Pill tone={STATUT_TONES[d.status] ?? "neutral"}>{d.statusLabel}</Pill>}
      >
        <FactList
          facts={[
            { id: "demandee-le", label: "Demandée le", value: d.requestedAt ? tools.formatDate(d.requestedAt, "dd/MM/yyyy à HH:mm") : null },
            { id: "par", label: "Demandée par", value: demandeur(d) },
            { id: "justificatif", label: "Justificatif France Travail", value: d.partnerJustification || null },
          ].filter((f) => f.value)}
        />
      </FicheHeader>

      {s && (
        <FicheSection id="sanction-visee" title="Suspension rattachée">
          <FactList
            facts={[
              { id: "sanction", label: "Sanction", value: s.label },
              { id: "ep", label: "Décidée à l'EP du", value: s.epDate ? tools.formatDate(s.epDate) : null },
              { id: "notif", label: "Notifiée le", value: s.notifiedAt ? tools.formatDate(s.notifiedAt) : null },
              { id: "fin", label: "Fin de période", value: s.endOfPeriod ? tools.formatDate(s.endOfPeriod) : null },
            ].filter((f) => f.value)}
          />
          {/* L'inscription à venir a été annulée dès l'enregistrement (15/09/2026) : dit sous la suspension. */}
          {!saisine && r && (
            <p className={`suspension-remobilisation__mention suspension-remobilisation__mention--${r.done ? "ko" : "warn"} suspension-remobilisation__inscription-annulee`}>
              <i className="fa fa-calendar-times mr-1" aria-hidden="true" />
              {r.done ? (
                <>Inscription EP du {tools.formatDate(r.epDate)} — annulée</>
              ) : (
                <>Inscription EP du {tools.formatDate(r.epDate)} — <strong>non annulée</strong> : {r.reasonNotDone}</>
              )}
            </p>
          )}
        </FicheSection>
      )}

      {saisine && (
        <FicheSection id="inscription-visee" title="Inscription à retirer de la commission">
          {i ? (
            <FactList
              facts={[
                { id: "ep", label: "Commission", value: `EP du ${tools.formatDate(i.epDate)}` },
                { id: "prop", label: "Mesure proposée", value: i.label },
              ]}
            />
          ) : (
            <p className="mb-0 text-muted">Aucune commission concernée : la levée de saisine sera simplement enregistrée.</p>
          )}
          {r && (
            <div className={`alert ${r.done ? "alert-success" : "alert-warning"} mt-2 mb-0`}>
              {r.done ? (
                <>Inscription à l'EP du {tools.formatDate(r.epDate)} retirée de l'ordre du jour.</>
              ) : (
                <>Inscription à l'EP du {tools.formatDate(r.epDate)} <strong>non retirée</strong> : {r.reasonNotDone}</>
              )}
            </div>
          )}
        </FicheSection>
      )}

      {d.reasonForLiftingLabel && (
        <FicheSection id="motif-ru" title="Motif de la demande">
          <p className="mb-0">{d.reasonForLiftingLabel}</p>
        </FicheSection>
      )}

      {d.directorDecision && (
        <FicheSection id="decision" title="Décision du Directeur">
          <FactList
            facts={[
              { id: "statut", label: "Décision", value: d.statusLabel },
              { id: "par", label: "Par", value: nomComplet(d.directorDecision.by) || d.directorDecision.by?.id },
              { id: "le", label: "Le", value: tools.formatDate(d.directorDecision.at, "dd/MM/yyyy à HH:mm") },
              { id: "motif-refus", label: "Motif du refus", value: d.directorDecision.refusalReasonLabel },
            ].filter((f) => f.value)}
          />
        </FicheSection>
      )}

      {decidable && (
        <FicheSection id="decider" title="Votre décision">
          <p className="text-muted small mb-2">
            {saisine ? (
              <>
                Valider retire l'allocataire de la commission visée. Refuser laisse l'inscription en place.
              </>
            ) : (
              <>
                Valider lève la suspension : l'allocataire sera notifié et la liquidation recevra un bon de travail.
                Refuser laisse la sanction s'appliquer, sans notification à l'allocataire.
              </>
            )}{" "}
            La décision et son motif restent visibles dans le parcours.
          </p>
          {refusOuvert && r?.done && (
            <div className="alert alert-warning suspension-remobilisation__avertissement-refus">
              Cet allocataire était inscrit à l'EP du {tools.formatDate(r.epDate)} ; cette inscription a été annulée à
              l'enregistrement de la demande. Un refus ne le réinscrit pas : une <strong>réinscription manuelle</strong> sera
              nécessaire pour relancer la procédure.
            </div>
          )}
          {confirmationOuverte && (
            <div className="alert alert-info suspension-remobilisation__confirmation">
              <strong>Confirmer la validation ?</strong>{" "}
              {saisine
                ? "L'allocataire sera retiré de la commission visée."
                : "L'allocataire sera notifié par courrier et la liquidation recevra un bon de travail."}
              <div className="d-flex justify-content-end mt-2">
                <button type="button" className="btn btn-link mr-2" disabled={enCours !== null} onClick={() => setConfirmationOuverte(false)}>
                  Revenir
                </button>
                <button
                  type="button"
                  className="btn btn-success suspension-remobilisation__confirmer-validation"
                  disabled={enCours !== null}
                  onClick={() => decider("validation")}
                >
                  <i className="fa fa-check mr-1" aria-hidden="true" />
                  Confirmer la validation
                </button>
              </div>
            </div>
          )}
          {refusOuvert && (
            <div className="form-group">
              <label htmlFor="suspension-remobilisation-motif-refus" className="font-weight-bold">Motif du refus</label>
              <select
                id="suspension-remobilisation-motif-refus"
                className="form-control suspension-remobilisation__motif-refus"
                value={motifRefus}
                onChange={(e) => setMotifRefus(e.target.value)}
              >
                <option value="">— Choisir un motif —</option>
                {MOTIFS_REFUS.map((m) => (
                  <option key={m.id} value={m.id}>{m.label}</option>
                ))}
              </select>
            </div>
          )}
          <div className="d-flex justify-content-end align-items-center">
            {refusOuvert ? (
              <>
                <button type="button" className="btn btn-link mr-2" disabled={enCours !== null} onClick={() => setRefusOuvert(false)}>
                  Annuler le refus
                </button>
                <button
                  type="button"
                  className="btn btn-outline-danger mr-2 suspension-remobilisation__refuser"
                  disabled={enCours !== null || !motifRefus}
                  onClick={() => decider("refus")}
                >
                  <i className="fa fa-times mr-1" aria-hidden="true" />
                  Confirmer le refus
                </button>
              </>
            ) : (
              <button type="button" className="btn btn-link text-danger mr-2 suspension-remobilisation__ouvrir-refus" disabled={enCours !== null || confirmationOuverte} onClick={() => setRefusOuvert(true)}>
                Refuser…
              </button>
            )}
            <button
              type="button"
              className="btn btn-success suspension-remobilisation__valider"
              disabled={enCours !== null || refusOuvert || confirmationOuverte}
              onClick={() => setConfirmationOuverte(true)}
            >
              <i className="fa fa-check mr-1" aria-hidden="true" />
              Valider
            </button>
          </div>
        </FicheSection>
      )}
    </div>
  );
};

export default DetailDemandeSuspensionRemobilisation;
